import * as React from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { PosologyTypeEnum } from "common/enums/PosologyTypeEnum";
import {
  MedicationEntryPrescriptionStatusEnum,
  MedicationEntryType,
} from "model/medication-entry.model";
import { PosologyType } from "model/posology.model";
import { StatusTypeEnum } from "common/enums/StatusTypeEnum";
import IconType from "@cc/react-components/IconType";
import { Col, Row } from "@cc/react-components/Grid";
import Button from "@cc/react-components/Button";
import ListGroup from "@cc/react-components/ListGroup";
import { Form } from "@cc/react-components";

export interface Props extends React.HTMLAttributes<HTMLElement> {
  medicationEntry: MedicationEntryType;
  selected: boolean;
  onEditMedicationEntry?: (medicationEntry: MedicationEntryType) => void;
}

const MedicationRow = ({
  medicationEntry,
  onClick,
  onEditMedicationEntry,
  selected,
}: Props) => {
  const [t] = useTranslation();

  const shouldDisable = (medicationEntry: MedicationEntryType): boolean => {
    return (
      !medicationEntry.posology.posologyText ||
      medicationEntry.posology.type === PosologyTypeEnum.DATE ||
      medicationEntry.posology.type === PosologyTypeEnum.PHASING
    );
  };
  const icon = getIcon(medicationEntry, selected);
  const disabled = shouldDisable(medicationEntry);

  return (
    <ListGroup.Item
      disabled={disabled}
      id={medicationEntry.id + ""}
      icon={icon}
    >
      <Row className="align-items-center m-0">
        <Col
          xs={9}
          className="pl-0"
          onClick={(e: any) =>
            !shouldDisable(medicationEntry) && onClick && onClick(e)
          }
        >
          <div className="font-weight-bold">
            {medicationEntry.freeTextContent}
          </div>
          {getPosology(medicationEntry.posology, t)}
        </Col>
        <Col xs={1}>
          {selected && (
            <Button
              variant="borderless-primary"
              icon="edit"
              onClick={() =>
                onEditMedicationEntry && onEditMedicationEntry(medicationEntry)
              }
            />
          )}
        </Col>
        <Col xs={1}>
          {!disabled && (
            <Form.Check
              id={"MedicationRowCheckbox-" + medicationEntry.id}
              checked={selected}
              onChange={(e: any) =>
                !shouldDisable(medicationEntry) && onClick && onClick(e)
              }
            />
          )}
        </Col>
      </Row>
    </ListGroup.Item>
  );
};

const getIcon = (
  medicationEntry: MedicationEntryType,
  selected: boolean
): IconType | "" => {
  if (medicationEntry.status === StatusTypeEnum.NOT_RELEVANT) {
    return "remove";
  } else if (
    selected &&
    medicationEntry.prescriptionStatus ===
      MedicationEntryPrescriptionStatusEnum.REPEAT
  ) {
    return "duplicate";
  } else if (
    medicationEntry.prescriptionStatus ===
    MedicationEntryPrescriptionStatusEnum.NEW
  ) {
    return "add";
  } else if (
    medicationEntry.prescriptionStatus ===
    MedicationEntryPrescriptionStatusEnum.EDITED
  ) {
    return "edit";
  }
  return "";
};

const getPosology = (posology: PosologyType, t: TFunction): string => {
  switch (posology.type) {
    case PosologyTypeEnum.FREE_TEXT:
      return concatPosology(posology.posologyText);
    case PosologyTypeEnum.SIMPLE:
      return posology.posologyText;
    default:
      return concatPosology(
        t("medicationOverview.schema") + " " + posology.posologyText
      );
  }
};

const concatPosology = (posologyText: string): string => {
  const MAX_LENGTH = 60;
  if (posologyText && posologyText !== "") {
    if (posologyText.length > MAX_LENGTH) {
      return posologyText.slice(0, MAX_LENGTH) + "...";
    }
    return posologyText;
  }
  return "";
};

export default MedicationRow;
